import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Form, Grid, Input } from 'semantic-ui-react';
import { checkPrinter } from 'services/printer.service';
import { useAlert } from 'context/AlertProvider';
import { useZebra } from 'context/ZebraProvider';
import { isSATPrinter } from 'modules/user-has-permission/user-has-permission';
import Portal from 'components/Portal/Portal';
import RequestManifest from 'components/RequestManifest/RequestManifest';
import { getByShipment } from 'services/shipment-labels.service';
import { printLabelCollection } from 'modules/print-label-collection/print-label-collection';
import { getMessageFromAxiosError } from 'modules/message-factory/message-factory';
import './print-label-form.scss';

export default function PrintLabelForm({
  isPrinting,
  setIsPrinting,
  placeholder = 'Escanea referencia de seguimiento',
}) {
  const alert = useAlert();
  const zebra = useZebra();
  const [reference, setReference] = useState('');
  const [groupedManifestOrders, setGroupedManifestOrders] = useState([]);
  const pistolInput = useRef();
  const resetInput = useCallback(() => {
    setReference('');
    pistolInput.current.focus();
    setIsPrinting(false);
  }, [setReference, pistolInput, setIsPrinting]);
  const userCanDownloadManifest = useMemo(() => isSATPrinter(), []);

  return (
    <Grid centered columns={2}>
      <Grid.Column>
        <Form>
          <Form.Field className="print-label-form">
            <Input
              ref={pistolInput}
              onChange={(event, data) => {
                setReference(data.value);
              }}
              size="big"
              value={reference}
              autoFocus
              placeholder={placeholder}
              action={{
                color: 'blue',
                labelPosition: 'right',
                disabled: isPrinting || !reference,
                loading: isPrinting,
                icon: 'print',
                content: 'Imprimir',
                onClick: async () => {
                  setIsPrinting(true);
                  const isAvailable = zebra ? await checkPrinter(zebra) : false;
                  if (!isAvailable) {
                    alert.current.error(
                      'Parece que la impresora por defecto no esta disponible, revise que esta encendida y conectada e intentelo de nuevo.',
                    );
                    setIsPrinting(false);
                    return;
                  }

                  try {
                    const { data: labels } = await getByShipment(reference);
                    const label_ids = labels?.map((label) => label.id);
                    await printLabelCollection(label_ids, reference, zebra);
                    setGroupedManifestOrders([
                      ...new Set([...groupedManifestOrders, reference]),
                    ]);
                  } catch (error) {
                    alert.current.error(getMessageFromAxiosError(error));
                  } finally {
                    resetInput();
                  }
                },
              }}
            />
          </Form.Field>
        </Form>
        {userCanDownloadManifest && !!groupedManifestOrders?.length && (
          <Portal.In target="card-header">
            <RequestManifest
              shipments={groupedManifestOrders}
              onManifestDownloaded={() => {
                setGroupedManifestOrders([]);
                setTimeout(() => {
                  pistolInput?.current?.focus();
                }, 400);
              }}
            />
          </Portal.In>
        )}
      </Grid.Column>
    </Grid>
  );
}
